<template>
  <sc-page-base :title="title">
    <sc-grid
      ref="form"
      :data="invoices"
      :total="invoices.length"
      :loading="loading"
      :rules="rules"
      :default-filters="defaultFilters"
      :has-backend-sorting="false"
      @submit="getInvoices">

      <template slot="filters" slot-scope="{ filters }">
        <div class="columns">
          <div class="column">
            <sc-field label="Tipo de fatura" prop="invoice_type">
              <sc-select
                v-model="filters.invoice_type"
                @input="setCurrentInvoiceType(filters.invoice_type)"
                expanded>
                <option selected value="INVOICE">Fatura</option>
                <option value="PRE_INVOICE">Pré-fatura</option>
              </sc-select>
            </sc-field>
          </div>

          <div class="column">
            <sc-field
              :label="currentInvoiceType.label"
              prop="invoice_numbers">
              <sc-taginput
                placeholder="Informe um ou mais valores"
                v-model="filters.invoice_numbers"
                :on-paste-separators="[',', ' ']"
                :maxtags="500" />
            </sc-field>
          </div>

          <div class="column">
            <sc-field label="Buscar periodo por" prop="date_range_type">
              <sc-select v-model="filters.date_range_type" expanded>
                <option selected value="GENERATION">Data de geração</option>
                <option value="EXPIRATION">Data de expiração</option>
              </sc-select>
            </sc-field>
          </div>

          <div class="column">
            <sc-field label="Período" prop="date_range">
              <sc-range-date-picker v-model="filters.date_range" />
            </sc-field>
          </div>
         
        </div>

        <div class="columns">

          <div class="column">
            <sc-field label="Transportadora" prop="logistic_providers">
              <sc-logistic-provider-dropdown
                v-model="filters.logistic_providers"
                @selectAll="(values) => filters.logistic_providers = values"
                @clearFilter="() => filters.logistic_providers = []"
                dropdown />
            </sc-field>
          </div>

          <div class="column">
            <sc-field label="Buscar por" prop="search_by">
              <sc-select
                v-model="filters.search_by"
                @input="setCurrentSearch(filters.search_by)"
                expanded>
                <option selected value="ORDER_NUMBER">Pedido</option>
                <option value="INVOICE_NUMBER">Nota Fiscal</option>
                <option value="INVOICE_KEY">Chave de acesso da NF</option>
                <option value="CTE_NUMBER">CTe</option>
                <option value="CTE_KEY">Chave de acesso do CTe</option>
              </sc-select>
            </sc-field>
          </div>

          <div class="column">
            <sc-field :label="currentSearch.label" prop="search_values">
              <sc-taginput
                placeholder="Informe um ou mais valores"
                v-model="filters.search_values"
                :on-paste-separators="[',', ' ']"
                :maxtags="500" />
            </sc-field>
          </div>
        </div>
      </template>

      <template slot="filters-actions" slot-scope="{ filters, isValid }">
        <sc-button
          text="Exportar para o Excel"
          :loading="loadingExports"
          :disabled="!isValid"
          @click="callExportToExcel(filters)" />

        <sc-button
          text="Gerar Espelho da Fatura"
          :loading="loadingInvoice"
          :disabled="!isValid"
          @click="generateInvoiceMirror(filters)" />

        <sc-button
          type="button"
          text="Limpar filtros"
          @click="clearFilters"/>

        <sc-button
          type="submit"
          theme="primary"
          text="Buscar"
          :loading="loading" />
      </template>

      <template slot="table-columns" slot-scope="{ props }">
        <sc-table-column
          label="ID"
          field="id"
          sortable>
          <a
            :href="`/recon/invoice-detail/${props.row.id}`"
            class="sc-recon-pre-invoice-table__table__link">
            {{ props.row.id }}
          </a>
        </sc-table-column>

        <sc-table-column
          label="Tipo da Fatura"
          field="invoice_type"
          sortable>
          {{ props.row.invoice_type.label }}
        </sc-table-column>

        <sc-table-column
          label="Transportadora"
          field="logistic_provider"
          sortable>
          {{ props.row.logistic_provider }}
        </sc-table-column>

        <sc-table-column
          label="Geração"
          field="generation_date"
          sortable>
          {{ props.row.generation_date | date('DD/MM/YYYY') }}
        </sc-table-column>

        <sc-table-column
          label="Expiração"
          field="expiration_date"
          sortable>
          {{ props.row.expiration_date | date('DD/MM/YYYY') }}
        </sc-table-column>

        <sc-table-column
          label="Qtd. NFs"
          field="invoice_count"
          sortable>
          {{ props.row.invoice_count }}
        </sc-table-column>

        <sc-table-column
          label="Valor da NF"
          field="invoice_value"
          sortable>
          {{ props.row.invoice_value | currency }}
        </sc-table-column>

        <sc-table-column
          label="Usuário"
          field="user"
          sortable>
          {{ props.row.user }}
        </sc-table-column>
      </template>
    </sc-grid>
  </sc-page-base>
</template>

<script>
import {
  ScButton,
  ScPageBase,
  ScGrid,
  ScField,
  ScTaginput,
  ScInput,
  ScLogisticProviderDropdown,
  ScSelect,
  ScRangeDatePicker,
  ScTable,
  ScTableColumn,
  ScTablePerPage,
  ScToast,
} from '@intelipost-js/secure-components';
import { Moment } from '@intelipost-js/secure-providers';
import { createNamespacedHelpers } from 'vuex';

import searchValueFieldsEnum from './enum/seach-value-fields.enum';
import invoiceTypeFieldsEnum from './enum/invoice-type-fields.enum';

const {
  mapState,
  mapActions,
} = createNamespacedHelpers('recon/invoice');

export default {
  name: 'page-invoice',
  data() {
    return {
      title: 'Consulta de Pré-Fatura e Fatura',
      currentSearch: searchValueFieldsEnum.ORDER_NUMBER,
      currentInvoiceType: invoiceTypeFieldsEnum.INVOICE,
      defaultFilters: {
        invoice_type: 'INVOICE',
        invoice_numbers: [],
        logistic_providers: [],
        date_range_type: 'GENERATION',
        date_range: {
          start: Moment()
            .subtract(1, 'days')
            .format('YYYY-MM-DD'),
          end: Moment().format('YYYY-MM-DD'),
        },
        search_by: 'ORDER_NUMBER',
        search_values: [],
      },
    };
  },
  computed: {
    ...mapState([
      'form',
      'invoices',
      'loading',
      'loadingExports',
      'loadingInvoice',
    ]),
    searchValuesOptions() {
      return searchValueFieldsEnum;
    },
    currentInvoiceTypeOptions() {
      return invoiceTypeFieldsEnum;
    },
    rules() {
      return {
        invoice_type: [
          {
            required: true,
            message: 'Selecione um tipo de fatura',
          },
        ],
        date_range: [
          {
            required: true,
            message: 'Selecione uma data',
          },
        ],
      };
    },
  },
  methods: {
    ...mapActions([
      'getInvoices',
      'exportToExcel',
      'sendInvoiceMirror',
    ]),
    setCurrentSearch(search_by) {
      const { order_number } = this.searchValuesOptions || {};

      this.currentSearch = this.searchValuesOptions[search_by] || order_number;
    },
    setCurrentInvoiceType(invoice_type) {
      const { INVOICE } = this.currentInvoiceTypeOptions || {};

      this.currentInvoiceType = this.currentInvoiceTypeOptions[invoice_type] || INVOICE;
    },
    clearFilters() {
      this.$refs.form.clearFilters();
    },
    callExportToExcel(filters) {
      this.exportToExcel(filters).then(() =>
        ScToast.success('Relatório está sendo gerado, em breve estará disponível em Meus Arquivos.'),
      );
    },
    generateInvoiceMirror(filters) {
      this.sendInvoiceMirror(filters).then(() =>
        ScToast.success('Espelho da fatura gerado com sucesso.'),
      );
    },
  },
  components: {
    ScButton,
    ScPageBase,
    ScGrid,
    ScField,
    ScTaginput,
    ScInput,
    ScLogisticProviderDropdown,
    ScSelect,
    ScRangeDatePicker,
    ScTable,
    ScTableColumn,
    ScTablePerPage,
  },
};
</script>

<style lang="scss" scoped>
.page-container {
  max-width: 100%;
}
</style>


// WEBPACK FOOTER //
// node_modules/@intelipost-modules/secure-recon/features/invoice/PageInvoice.vue